import i18next, { type i18n } from 'i18next';
import { uk } from './uk';

export { uk, type Translation } from './uk';

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'translation';
    resources: { translation: typeof uk };
  }
}

type PluralCategory = 'one' | 'few' | 'many' | 'other';

/** CLDR cardinal plural rule of Ukrainian: 1 урок, 2 уроки, 5 уроків, 1,5 уроку. */
export function ukrainianPlural(n: number): PluralCategory {
  if (!Number.isInteger(n)) return 'other';
  const mod10 = Math.abs(n) % 10;
  const mod100 = Math.abs(n) % 100;
  if (mod10 === 1 && mod100 !== 11) return 'one';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
  return 'many';
}

/**
 * Hermes (the app's JavaScript engine) has no Intl.PluralRules, and i18next then knows only
 * «one» and «other» — «7 уроку» instead of «7 уроків». Browsers and Node have it: no-op there.
 */
function ensurePluralRules() {
  try {
    if (new Intl.PluralRules('uk').select(2) === 'few') return;
  } catch {
    // missing: fall through to the Ukrainian rule
  }
  class UkrainianPluralRules {
    readonly type: 'cardinal' | 'ordinal';
    constructor(_locales?: string | string[], options?: { type?: 'cardinal' | 'ordinal' }) {
      this.type = options?.type ?? 'cardinal';
    }
    select(n: number): PluralCategory {
      return this.type === 'ordinal' ? 'other' : ukrainianPlural(n);
    }
    resolvedOptions() {
      const pluralCategories: PluralCategory[] =
        this.type === 'ordinal' ? ['other'] : ['one', 'few', 'many', 'other'];
      return { locale: 'uk', type: this.type, pluralCategories };
    }
    static supportedLocalesOf(locales: string | string[]) {
      return ([] as string[]).concat(locales).filter((l) => l.startsWith('uk'));
    }
  }
  const intl = (globalThis as { Intl?: Record<string, unknown> }).Intl;
  if (intl) intl.PluralRules = UkrainianPluralRules;
  else (globalThis as { Intl?: unknown }).Intl = { PluralRules: UkrainianPluralRules };
}

/** Creates an isolated i18next instance (Ukrainian only for now; English can be added later). */
export async function createI18n(
  extraPlugins: ReadonlyArray<Parameters<i18n['use']>[0]> = [],
): Promise<i18n> {
  ensurePluralRules();
  const instance = i18next.createInstance();
  for (const plugin of extraPlugins) instance.use(plugin);
  await instance.init({
    lng: 'uk',
    fallbackLng: 'uk',
    supportedLngs: ['uk'],
    resources: { uk: { translation: uk } },
    interpolation: { escapeValue: false }, // React escapes values itself
    returnNull: false,
  });
  return instance;
}
